import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import { BFFClient } from '~settings/api/lib/BFFClient';
import type { SearchVariables } from '~settings/api/types/searchVariables';

import { LabelGroupQueryKeys } from '../config/queryKeys';
import { getCachedListResultByIds } from '../lib/cache';
import { getRequestHeaders } from '../lib/headers';
import {
  type LabelGroupListResponse,
  LabelGroupListResponseSchema,
} from '../lib/schemas';
import type { PresetUseQueryOptions } from '../lib/types';
import type { PaginationVariables } from '../types/paginationVariables';

export interface LabelGroupListQueryVariables
  extends PaginationVariables,
    SearchVariables {
  labelGroupIds?: string;
  userId?: string;
  withSpecialGroups?: boolean;
}

export function useLabelGroupList<TData = LabelGroupListResponse>(
  variables: LabelGroupListQueryVariables,
  options?: PresetUseQueryOptions<LabelGroupListResponse, TData>,
) {
  const session = useWmgSession();
  const queryClient = useQueryClient();
  const isCurrentUserInternal = !useIsExternalUser();

  const { data, ...query } = useQuery({
    queryKey: LabelGroupQueryKeys.list(variables),
    queryFn: async () => {
      if (variables.labelGroupIds?.length) {
        const cachedResult = getCachedListResultByIds(
          queryClient,
          variables.labelGroupIds,
          'labelGroup',
        );

        if (cachedResult) {
          return LabelGroupListResponseSchema.parse(cachedResult);
        }
      }

      const responseData = await BFFClient.get('label-groups', {
        headers: getRequestHeaders(session.token),
        searchParams: { ...variables },
      }).json();

      return LabelGroupListResponseSchema.parse(responseData);
    },
    placeholderData: keepPreviousData,
    ...options,
    enabled: isCurrentUserInternal && options?.enabled !== false,
  });

  return { labelGroupList: data, ...query };
}
